import React, { useEffect, useState } from "react";
import { AppShell } from "../components/cod/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { adminApi, adminInviteApi } from "../lib/api";
import { toast } from "sonner";
import { UserPlus, RefreshCw, Copy } from "lucide-react";

const INVITE_ROLES = [
  ["staff", "Staff Member"],
  ["admin", "Administrator"],
  ["instructor", "Instructor"],
  ["employer", "Employer"],
  ["partner", "Partner"],
  ["community_partner", "Community Partner"],
  ["training_provider", "Training Provider"],
  ["government_agency", "Government Agency"],
  ["funding_org", "Funding Organization"],
  ["volunteer", "Volunteer"],
];

export default function AdminConsole() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [audit, setAudit] = useState([]);
  const [invites, setInvites] = useState([]);
  const [showInvite, setShowInvite] = useState(false);

  const loadAll = () => {
    adminApi.stats().then(setStats);
    adminApi.users().then(setUsers);
    adminApi.audit().then(setAudit);
    adminInviteApi.list().then(setInvites).catch(()=>setInvites([]));
  };
  useEffect(() => { loadAll(); }, []);

  const cards = stats ? [
    { label: "Participants", value: stats.participants },
    { label: "Staff", value: stats.staff },
    { label: "Partners/Employers", value: stats.partners },
    { label: "Active Plans", value: stats.active_plans },
    { label: "Conversations", value: stats.conversations },
    { label: "Documents", value: stats.documents },
    { label: "Open Referrals", value: stats.open_referrals },
    { label: "P1 Escalations", value: stats.escalations_p1, accent: true },
    { label: "P2 Escalations", value: stats.escalations_p2 },
  ] : [];

  return (
    <AppShell>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="font-serif text-3xl text-slate-900">Admin Console</h1>
          <Button className="cod-bg-red" onClick={()=>setShowInvite(true)} data-testid="btn-invite-user"><UserPlus size={16} className="mr-1"/> Invite User</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {cards.map(c => (
            <Card key={c.label} data-testid={`stat-${c.label.toLowerCase().replace(/[^a-z]/g,"")}`}>
              <CardContent className="py-5">
                <div className="text-xs uppercase tracking-wider text-slate-500">{c.label}</div>
                <div className={`text-3xl font-serif mt-1`} style={{ color: c.accent ? "var(--cod-red)" : "var(--cod-navy)" }}>{c.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        <Tabs defaultValue="users">
          <TabsList>
            <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
            <TabsTrigger value="invitations" data-testid="admin-tab-invitations">Pending Invitations ({invites.length})</TabsTrigger>
            <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
          </TabsList>
          <TabsContent value="users" className="mt-4">
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Active</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
                  <TableBody>{users.map(u => (
                    <TableRow key={u.id} data-testid={`user-row-${u.id}`}><TableCell className="whitespace-nowrap">{u.full_name}</TableCell><TableCell className="whitespace-nowrap">{u.email}</TableCell><TableCell><Badge variant="outline">{u.role}</Badge></TableCell><TableCell>{u.active?"Yes":"No"}</TableCell><TableCell className="text-sm text-slate-500 whitespace-nowrap">{new Date(u.created_at).toLocaleDateString()}</TableCell></TableRow>
                  ))}</TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
          <TabsContent value="invitations" className="mt-4">
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Invited</TableHead><TableHead>Email sent?</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
                  <TableBody data-testid="invitations-list">
                    {invites.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-500 py-6">No pending invitations. Click &quot;Invite User&quot; to send one.</TableCell></TableRow>
                    )}
                    {invites.map(u => (
                      <TableRow key={u.id} data-testid={`invite-row-${u.id}`}>
                        <TableCell className="whitespace-nowrap">{u.full_name}</TableCell>
                        <TableCell className="whitespace-nowrap">{u.email}</TableCell>
                        <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                        <TableCell className="text-sm text-slate-500">{u.invited_at ? new Date(u.invited_at).toLocaleDateString() : "—"}</TableCell>
                        <TableCell>{u.invitation_email_result?.sent ? <span className="text-emerald-700 text-xs">Sent</span> : <span className="text-amber-700 text-xs">Not sent</span>}</TableCell>
                        <TableCell><ResendInviteButton uid={u.id} onDone={loadAll}/></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </TabsContent>
          <TabsContent value="audit" className="mt-4">
            <Card><CardContent className="pt-6 space-y-1 max-h-[60vh] overflow-y-auto scrollbar-thin">
              {audit.map(a => (
                <div key={a.id} className="text-sm border-b py-1.5 flex justify-between">
                  <span><strong>{a.action}</strong> <span className="text-slate-500">by {a.actor_id?.slice(0,8)}</span></span>
                  <span className="text-xs text-slate-500">{new Date(a.ts).toLocaleString()}</span>
                </div>
              ))}
            </CardContent></Card>
          </TabsContent>
        </Tabs>
      </div>

      <InviteDialog open={showInvite} onOpenChange={setShowInvite} onSent={loadAll}/>
    </AppShell>
  );
}

function ResendInviteButton({ uid, onDone }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const send = async () => {
    setBusy(true);
    try {
      const r = await adminInviteApi.resend(uid);
      setResult(r);
      toast.success(r.email_sent ? "Invitation resent" : "Invite regenerated — copy the temporary password");
      onDone && onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" variant="outline" onClick={send} disabled={busy} data-testid={`invite-resend-${uid}`}><RefreshCw size={12} className="mr-1"/>Resend</Button>
      {result && !result.email_sent && (
        <Dialog open onOpenChange={()=>setResult(null)}>
          <DialogContent>
            <DialogHeader><DialogTitle>Manual Invitation Details</DialogTitle></DialogHeader>
            <div className="text-sm text-slate-700">Resend email delivery is not configured. Share these credentials with the invitee manually:</div>
            <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Temporary password</div>
                <div className="flex items-center gap-2">
                  <code className="text-sm bg-white border border-slate-200 px-2 py-1 rounded flex-1">{result.temporary_password}</code>
                  <Button size="sm" variant="outline" onClick={()=>{ navigator.clipboard.writeText(result.temporary_password); toast.success("Copied"); }}><Copy size={12}/></Button>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sign-in URL</div>
                <code className="text-xs text-slate-600 break-all">{result.invite_url}</code>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

function InviteDialog({ open, onOpenChange, onSent }) {
  const [form, setForm] = useState({ email: "", full_name: "", role: "staff", phone: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await adminInviteApi.invite(form);
      setResult(r);
      toast.success(r.email_sent ? `Invitation email sent to ${form.email}` : "Account created — share the temporary password manually");
      onSent && onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Invite failed"); }
    finally { setBusy(false); }
  };
  const close = () => { setForm({ email: "", full_name: "", role: "staff", phone: "" }); setResult(null); onOpenChange(false); };
  return (
    <Dialog open={open} onOpenChange={(v)=>{ if(!v) close(); else onOpenChange(v); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{result ? "Invitation Result" : "Invite New User"}</DialogTitle></DialogHeader>
        {!result && (
          <form onSubmit={submit} className="space-y-4">
            <div><Label>Full name</Label><Input required value={form.full_name} onChange={e=>setForm({...form, full_name:e.target.value})} data-testid="invite-name"/></div>
            <div><Label>Email</Label><Input type="email" required value={form.email} onChange={e=>setForm({...form, email:e.target.value})} data-testid="invite-email"/></div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={v=>setForm({...form, role:v})}>
                <SelectTrigger data-testid="invite-role"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {INVITE_ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Phone (optional)</Label><Input value={form.phone} onChange={e=>setForm({...form, phone:e.target.value})} data-testid="invite-phone"/></div>
            <div className="text-xs text-slate-500 rounded bg-slate-50 border border-slate-200 p-3">
              An invitation email will be sent via Resend with a temporary password. The invitee must reset it on first login. If Resend is not configured, the credentials are shown here for manual sharing.
            </div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={close}>Cancel</Button><Button type="submit" className="cod-bg-red" disabled={busy} data-testid="invite-submit">{busy ? "Inviting…" : "Send Invitation"}</Button></div>
          </form>
        )}
        {result && (
          <div className="space-y-3">
            <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
              {result.email_sent
                ? <>Invitation email sent to <strong>{result.user?.email}</strong>. The user must click the link and set a new password before accessing the platform.</>
                : <>Account created for <strong>{result.user?.email}</strong>. Resend email delivery is not configured, so share these credentials manually:</>
              }
            </div>
            {!result.email_sent && (
              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="invite-manual-creds">
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Temporary password</div>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="text-sm bg-white border border-slate-200 px-2 py-1 rounded flex-1">{result.temporary_password}</code>
                    <Button size="sm" variant="outline" onClick={()=>{ navigator.clipboard.writeText(result.temporary_password); toast.success("Copied"); }} data-testid="invite-copy-pw"><Copy size={12}/></Button>
                  </div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Sign-in URL</div>
                  <code className="text-xs text-slate-600 break-all">{result.invite_url}</code>
                </div>
              </div>
            )}
            <div className="flex justify-end"><Button onClick={close} data-testid="invite-close">Close</Button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
